import { useCallback, useEffect, useMemo, useState } from 'react';
import { RColumn, RemoteTable, stringFilterTemplate } from 'medere-ui-framework';
import { Button } from 'primereact/button';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { classNames } from 'primereact/utils';
import { Sector } from '../../interface/interface';
import { getStockSectors, StockSectorListParams } from '../../services/SectorRepositoryController';
import { parseApiError } from '../../services/apiError';
import NvTooltips from '../../components/novarium/NvTooltips';
import SectorRepositoryView from './SectorRepositoryView';
import './StockSectors.scss';

const TABLE_ID = 'StockSectorsTable';
const ROW_HEIGHT = 44;

const COLUMN_WIDTHS = {
    buildigname: 170,
    name: 190,
    shortname: 130,
    sectortypedescription: 160,
    repositorytypedescription: 180,
    externalcode: 130,
    parentsectorname: 200,
    prescriptionpriority: 110,
    restrictroomgendercontrol: 140,
    active: 130,
};
const ROW_ACTIONS_WIDTH = 50;
const TABLE_MIN_WIDTH = Object.values(COLUMN_WIDTHS).reduce((total, width) => total + width, ROW_ACTIONS_WIDTH);

const STATUS_OPTIONS = [
    { label: 'Activo', value: true },
    { label: 'Inactivo', value: false },
];

const DEFAULT_SORT = [{ field: 'buildigname', order: 1 as const }, { field: 'name', order: 1 as const }];

const orEmpty = (value: string | number | null | undefined) => (value == null || value === '' ? '—' : value);

function truncated(value: string | null | undefined) {
    if (value == null || value.trim() === '') return '—';
    return <span className="nv-truncate nv-tip" data-pr-tooltip={value}>{value}</span>;
}

function clearTableState() {
    [TABLE_ID, `mobileFilter_${TABLE_ID}`].forEach((key) => {
        try {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        } catch {
        }
    });
}

function hasColumnFilters(filterDTO: unknown): boolean {
    const filters = (filterDTO as { filters?: unknown[] } | null | undefined)?.filters;
    return Array.isArray(filters) && filters.length > 0;
}

export default function StockSectorsView() {
    const [activeFilter, setActiveFilter] = useState<boolean[]>([]);
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [configuring, setConfiguring] = useState<Sector | null>(null);

    useEffect(() => {
        clearTableState();
        return clearTableState;
    }, []);

    const tableRenderKey = `${TABLE_ID}-${activeFilter.join('|')}`;

    const loadSectors = useCallback(async (offSet: number, pageSize: number, params: StockSectorListParams = {}) => {
        if (pageSize > 0) setFiltersApplied(hasColumnFilters(params.filterDTO) || (params.activeFilter?.length ?? 0) > 0);
        try {
            return await getStockSectors(offSet, pageSize, params);
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            console.error(apiError.errorCode, apiError.path);
            throw error;
        }
    }, []);

    const tableParams = useMemo(() => ({ activeFilter }), [activeFilter]);

    const rowActions = (sector: Sector) => (
        <Button
            type="button"
            text
            icon="pi pi-box"
            className="nv-icon"
            aria-label={`Configurar repositorio de ${sector.name}`}
            tooltip="Configurar repositorio"
            tooltipOptions={{ position: 'left', event: 'both', className: 'nv-tooltip stock-sectors-tooltip' }}
            onClick={() => setConfiguring(sector)}
        />
    );

    const activeFilterElement = useCallback(() => (
        <MultiSelect
            options={STATUS_OPTIONS}
            optionLabel="label"
            optionValue="value"
            value={activeFilter}
            onChange={(e: MultiSelectChangeEvent) => setActiveFilter((e.value ?? []) as boolean[])}
            placeholder="Todos"
            className={classNames('w-full', { 'nv-filter-applied': activeFilter.length > 0 })}
            aria-label="Filtrar por estado"
        />
    ), [activeFilter]);

    if (configuring) {
        return <SectorRepositoryView sector={configuring} onBack={() => setConfiguring(null)} />;
    }

    return (
        <div className="nv-scope nv-page nv-page-fill stock-sectors-view">
            <NvTooltips />
            <div className="nv-page-head">
                <h1 className="nv-h1">Sectores</h1>
            </div>

            <div className="nv-table-fill">
                <RemoteTable
                    key={tableRenderKey}
                    idTable={TABLE_ID}
                    dataKey="sector"
                    className="nv-list-table"
                    getItems={loadSectors}
                    params={tableParams}
                    paramsDelay={0}
                    filterRow
                    defaultMultiSortMeta={DEFAULT_SORT}
                    onClearFilters={() => setActiveFilter([])}
                    itemSize={ROW_HEIGHT}
                    tableMinWidth={TABLE_MIN_WIDTH}
                    rowActions={rowActions}
                    notFoundText={filtersApplied ? 'No hay resultados para estos filtros.' : 'Todavía no hay sectores cargados.'}
                >
                    <RColumn field="buildigname" header="Establecimiento" grow={COLUMN_WIDTHS.buildigname} sortable filter filterElement={stringFilterTemplate} body={(s: Sector) => truncated(s.buildigname)} />
                    <RColumn field="name" header="Nombre" grow={COLUMN_WIDTHS.name} sortable filter filterElement={stringFilterTemplate} body={(s: Sector) => truncated(s.name)} />
                    <RColumn field="shortname" header="Abreviatura" grow={COLUMN_WIDTHS.shortname} className="text-center" sortable filter filterElement={stringFilterTemplate} body={(s: Sector) => orEmpty(s.shortname)} />
                    <RColumn field="sectortypedescription" header="Tipo de sector" grow={COLUMN_WIDTHS.sectortypedescription} sortable filter filterElement={stringFilterTemplate} body={(s: Sector) => truncated(s.sectortypedescription)} />
                    <RColumn field="repositorytypedescription" header="Tipo de repositorio" grow={COLUMN_WIDTHS.repositorytypedescription} sortable filter filterElement={stringFilterTemplate} body={(s: Sector) => truncated(s.repositorytypedescription)} />
                    <RColumn field="externalcode" header="Cód. externo" grow={COLUMN_WIDTHS.externalcode} className="text-center" sortable filter filterElement={stringFilterTemplate} body={(s: Sector) => orEmpty(s.externalcode)} />
                    <RColumn field="parentsectorname" header="Repositorio de trabajo" grow={COLUMN_WIDTHS.parentsectorname} sortable filter filterElement={stringFilterTemplate} body={(s: Sector) => truncated(s.parentsectorname)} />
                    <RColumn field="prescriptionpriority" header="Prioridad" grow={COLUMN_WIDTHS.prescriptionpriority} className="text-center" sortable body={(s: Sector) => orEmpty(s.prescriptionpriority)} />
                    <RColumn field="restrictroomgendercontrol" header="Control de sexo" grow={COLUMN_WIDTHS.restrictroomgendercontrol} className="text-center" body={(s: Sector) => (s.restrictroomgendercontrol ? 'Sí' : 'No')} />
                    <RColumn field="active" header="Estado" grow={COLUMN_WIDTHS.active} className="text-center" filter filterElement={activeFilterElement} body={(s: Sector) => (s.active ? 'Activo' : 'Inactivo')} />
                </RemoteTable>
            </div>
        </div>
    );
}
